/**
 * Connect POS — Impresión directa ESC/POS por Web Serial (sin diálogo del navegador).
 * Cubre impresoras USB con puerto COM virtual y Bluetooth clásico (SPP/RFCOMM)
 * emparejado en el sistema operativo. Reutiliza los builders ESC/POS de bluetoothPrint.
 * Soporte: Chrome/Edge escritorio (89+; Bluetooth RFCOMM 117+).
 */
import { buildTicketBytes, buildRechargeBytes, buildSeguroBytes } from "@/lib/bluetoothPrint";

const LS_KEY = "connect-serial-printer";
const SPP_UUID = "00001101-0000-1000-8000-00805f9b34fb";
export const SERIAL_BAUD_RATES = [9600, 19200, 38400, 57600, 115200];

let cachedPort = null;
let cachedBaud = null;

export function isWebSerialAvailable() {
  return typeof navigator !== "undefined" && !!navigator.serial && !window.electronAPI?.isElectron;
}

const describe = (port) => {
  const i = port?.getInfo?.() || {};
  if (i.bluetoothServiceClassId) return { kind: "bt", name: "Bluetooth clásico (SPP)" };
  if (i.usbVendorId != null) {
    const hex = (n) => n.toString(16).padStart(4, "0").toUpperCase();
    return { kind: "usb", name: `USB ${hex(i.usbVendorId)}:${hex(i.usbProductId ?? 0)}`, vid: i.usbVendorId, pid: i.usbProductId };
  }
  return { kind: "serial", name: "Puerto serie" };
};

export function getSavedSerialPrinter() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
}

async function openPort(port, baudRate) {
  const baud = Number(baudRate) || 9600;
  // Cambio de baudios: el puerto abierto a otra velocidad se cierra y reabre.
  if (port.writable && cachedPort === port && cachedBaud !== baud) await port.close();
  if (!port.writable) await port.open({ baudRate: baud });
  cachedPort = port;
  cachedBaud = baud;
  return port;
}

export async function pairSerialPrinter(baudRate = 9600) {
  if (!isWebSerialAvailable()) throw new Error("Web Serial no está disponible (usa Chrome o Edge de escritorio).");
  let port;
  try {
    port = await navigator.serial.requestPort({ allowedBluetoothServiceClassIds: [SPP_UUID] });
  } catch (e) {
    if (e?.name === "TypeError") port = await navigator.serial.requestPort();
    else throw e;
  }
  if (cachedPort && cachedPort !== port) { try { await cachedPort.close(); } catch { /* noop */ } }
  await openPort(port, baudRate);
  const info = describe(port);
  localStorage.setItem(LS_KEY, JSON.stringify(info));
  return { port, info };
}

// Puerto ya autorizado: no requiere gesto del usuario (sirve para auto-impresión).
async function getSerialPort(baudRate) {
  if (!isWebSerialAvailable()) throw new Error("Web Serial no disponible");
  if (cachedPort?.writable) return openPort(cachedPort, baudRate);
  const saved = getSavedSerialPrinter();
  const ports = await navigator.serial.getPorts();
  const match = ports.find((p) => {
    const d = describe(p);
    return saved && d.kind === saved.kind && (d.kind !== "usb" || (d.vid === saved.vid && d.pid === saved.pid));
  }) || (ports.length === 1 ? ports[0] : null);
  if (match) return openPort(match, baudRate);
  if (navigator.userActivation && !navigator.userActivation.isActive) {
    throw new Error("Impresora directa no vinculada. Ábrela en Impresora → Vincular puerto.");
  }
  return (await pairSerialPrinter(baudRate)).port;
}

async function writeBytes(port, bytes, chunk = 512) {
  const writer = port.writable.getWriter();
  try {
    for (let i = 0; i < bytes.length; i += chunk) {
      await writer.write(bytes.subarray(i, i + chunk));
      await new Promise((r) => setTimeout(r, 15));
    }
  } finally {
    writer.releaseLock();
  }
}

export async function forgetSerialPrinter() {
  localStorage.removeItem(LS_KEY);
  const port = cachedPort;
  cachedPort = null;
  try { await port?.close(); } catch { /* noop */ }
  try { await port?.forget?.(); } catch { /* noop */ }
}

async function send(build, ctx) {
  try {
    const port = await getSerialPort(ctx.baudRate);
    await writeBytes(port, build());
    return { ok: true, device: { name: getSavedSerialPrinter()?.name || "impresora directa" } };
  } catch (e) {
    cachedPort = null;
    return { ok: false, error: e?.message || String(e) };
  }
}

export const printTicketViaSerial = (ticket, ctx = {}) => send(() => buildTicketBytes(ticket, ctx), ctx);
export const printRechargeViaSerial = (rec, ctx = {}) => send(() => buildRechargeBytes(rec, ctx), ctx);
export const printSeguroViaSerial = (seg, ctx = {}) => send(() => buildSeguroBytes(seg, ctx), ctx);
